import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {ReviewThread} from '@github-ui/diff-lines/review-thread'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {memo, useMemo} from 'react'
import {ThreadHeader} from '../ThreadHeader'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import {useGetThread, useSelectedCommentReplyThreadId} from '../../../stores/layout-selectors'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../../page-data/loaders/use-thread-previews'
import {ThreadReplyComposer} from './ThreadReplyComposer'
import {useNavigateToFirstComment} from './use-navigate-to-first-comment'
import {NavigateToCommentButton} from './NavigateToCommentButton'
import {OpenThreadRepliesButton} from './OpenThreadRepliesButton'

function panelThreadCollapsedStateStorageKey(id: string) {
  return `panel-thread-collapsed-state-${id}`
}

type ThreadPreviewProps = {
  onNavigateToDiffComment: () => void
  tabSize?: number
  thread: Thread
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

/**
 * Renders a preview of a thread in a pull request.
 * It displays the thread's header, the difflines that the
 * thread is associated with, and the first comment of the thread.
 */
export const ThreadPreview = memo(function ThreadPreview({
  onNavigateToDiffComment,
  tabSize,
  thread,
  headingLevel,
}: ThreadPreviewProps) {
  const firstComment = getFirstComment(thread)
  const navigateToComment = useNavigateToFirstComment(thread, onNavigateToDiffComment)
  const getThreadFromStore = useGetThread()

  const {filePath, lineKey} = getThreadPreviewsLineAndFileKeysFromThread(thread)
  const storedThread = getThreadFromStore(Number(thread.id), filePath, lineKey)
  //TODO: if the stored thread is undefined, then add the thread to the store
  const isResolved = storedThread?.isResolved || thread.isResolved || false
  const isOutdated = thread.isOutdated || thread.subject?.isOutdated || thread.positioning?.isOutdated || false

  const [isCollapsed, setIsCollapsed] = useLocalStorage(panelThreadCollapsedStateStorageKey(thread.id), false)
  const isDockedCommentsRepliesEnabled = isFeatureEnabled('prs_docked_comments_replies')
  const selectedThreadId = useSelectedCommentReplyThreadId()
  const replyCount = Math.max((thread.commentsData?.comments.length ?? 1) - 1, 0)
  let diffLinesForPreview = thread.subject?.diffLines

  // if we don't have a start line, or it is the same as the end line, then we only want to show the diff line
  // the comment was made on.
  if (!thread.subject?.startLine || thread.subject?.startLine === thread.subject?.endLine) {
    diffLinesForPreview = thread.subject?.diffLines?.slice(-1)
  }
  const detailThread = useMemo(() => ({...thread, viewerCanReply: false}), [thread])
  // if the comment returned is a reply, this will be true
  if (!firstComment) return null

  return (
    <div className="border rounded-2 d-flex flex-column">
      <ThreadHeader
        firstCommentId={firstComment?.databaseId}
        isCollapsed={isCollapsed}
        thread={thread}
        onNavigateToDiffComment={navigateToComment}
        onToggleCollapsed={() => setIsCollapsed(!isCollapsed)}
        headingLevel={headingLevel}
        isResolved={isResolved}
        isOutdated={isOutdated}
        lineKey={lineKey}
      />
      {!isCollapsed && (
        <>
          <div className="border-bottom borderColor-muted overflow-x-auto">
            <StaticUnifiedDiffPreview
              diffTableClassName="border-0"
              diffLines={diffLinesForPreview}
              className="m-0 border-0"
              tabSize={tabSize || 4}
            />
          </div>
          {isDockedCommentsRepliesEnabled && selectedThreadId === thread.id ? (
            <>
              <ReviewThread
                lineMarkersKey={lineKey}
                batchingEnabled
                commentAnchorPrefix={'panel-r'}
                filePath={filePath}
                gridCellId={`${thread.id}-comments-panel-thread`}
                isInlineComment={false}
                thread={detailThread}
                shouldLimitHeight={false}
              />
              {thread.viewerCanReply && <ThreadReplyComposer thread={thread} startExpanded={replyCount === 0} />}
            </>
          ) : (
            <>
              <div>
                <ReviewThreadComment
                  lineMarkersKey={lineKey}
                  key={firstComment?.id}
                  hideActions
                  isInlineComment={false}
                  comment={firstComment}
                  filePath={filePath}
                  index={0}
                  isAnchorable={false}
                  isOutdated={isOutdated}
                  isThreadResolved={isResolved}
                  threadId={thread.id}
                  originalDiffPathUri={thread.originalDiffPathUri}
                  headingLevel="h3"
                  tooltipDirection="sw"
                />
              </div>
              <div className="mb-2 px-2">
                {isDockedCommentsRepliesEnabled ? (
                  <OpenThreadRepliesButton thread={thread} />
                ) : (
                  <NavigateToCommentButton thread={thread} onNavigateToDiffComment={onNavigateToDiffComment} />
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
})
